'use client';

import React from 'react';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Input } from '@eliadmin/core/src/components/ui/input';

// Helper to clamp a number between min and max.
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const ProductWithDimensionsDetailPrice = () => {
  const { t } = useTranslations();
  const settings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(state => state.product);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const variants = useVariantsStore(state => state.product.variants);
  const updateCustomValue = useVariantsStore(state => state.updateCustomValue);
  const setCustomValuesError = useVariantsStore(state => state.setCustomValuesError);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const me = useCustomerStore(state => state.me);

  const hasServiceRestriction = selectedServices.some(
    service => service?.name === 'OHYB OKRAJOV 20MM',
  );

  const getServicesPrice = (vat = false) => {
    let price = 0;
    selectedServices.forEach(service => {
      price += vat
        ? parseFloat(service?.price_with_vat.toString() || '2')
        : parseFloat(service?.price.toString() || '2');
    });
    return price;
  };

  const desiredObject = variants?.length ? selectedVariant : product;
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst: settings?.product_detail?.price_without_vat_first?.enabled,
  });
  const discount = priceDisplay.showPreviousPrice ? priceDisplay.discount : 0;
  const priceWithoutVatFirst = settings?.product_detail?.price_without_vat_first?.enabled === true;

  const unit = settings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';

  const renderErrorMessage = () => {
    if (product?.attributes?.length && selectedVariant == null) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground')}>
          {t('text495')}
        </span>
      );
    }
    return null;
  };

  // --- DIMENSIONS (range attributes) ---
  const rangeAttributes = React.useMemo(() => {
    return product?.attributes?.filter(attr => attr.type === 'range') || [];
  }, [product?.attributes]);

  // Local state for raw dimension inputs.
  const [dimensions, setDimensions] = React.useState<Record<string, string>>({});

  // On mount or when rangeAttributes change, initialize dimensions.
  React.useEffect(() => {
    const initialDimensions: Record<string, string> = {};
    rangeAttributes.forEach(attr => {
      const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
      let max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
      if (hasServiceRestriction) {
        max -= 0.038;
      }
      let defaultValue = 1;
      if (defaultValue < min) defaultValue = min;
      if (defaultValue > max) defaultValue = max;
      initialDimensions[attr.id] = defaultValue.toString();
      // Update the custom value in the store.
      updateCustomValue({
        id: attr.values?.[0]?.id as number,
        name: attr.name,
        unit: attr.values?.[0]?.name || 'm',
        value: defaultValue,
      });
    });
    setDimensions(initialDimensions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeAttributes, product, updateCustomValue]);

  // Memoized change handler.
  const handleDimensionChange = React.useCallback(
    (id: string, newValue: string) => {
      setDimensions(prev => {
        const newDimensions = { ...prev, [id]: newValue };
        const attribute = rangeAttributes.find(attr => attr.id.toString() === id.toString());
        if (attribute) {
          updateCustomValue({
            id: attribute.values?.[0]?.id as number,
            name: attribute.name,
            unit: attribute.values?.[0]?.name || 'm',
            value: isNaN(parseFloat(newValue)) ? newValue : parseFloat(newValue),
          });
        }
        return newDimensions;
      });
    },
    [rangeAttributes, updateCustomValue],
  );

  // Effect to check for errors across all custom values and update the store.
  React.useEffect(() => {
    const errors: string[] = [];
    rangeAttributes.forEach(attr => {
      const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
      let max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
      if (hasServiceRestriction) {
        max -= 0.038;
      }
      const currentValue = dimensions[attr.id];
      const parsed = parseFloat(currentValue);
      if (currentValue !== '' && !isNaN(parsed) && (parsed < min || parsed > max)) {
        errors.push(`${attr.name}: ${t('text1994')} ${min} - ${max}`);
      }
    });
    // Set the error in the store if any exist, otherwise clear it.
    if (errors.length > 0) {
      setCustomValuesError(true);
    } else {
      setCustomValuesError(false);
    }
  }, [
    dimensions,
    rangeAttributes,
    t,
    setCustomValuesError,
    selectedServices,
    hasServiceRestriction,
  ]);

  // Compute the multiplier.
  const multiplier = rangeAttributes.reduce((acc, attr) => {
    const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
    let max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
    if (hasServiceRestriction) {
      max -= 0.038;
    }
    const raw = parseFloat(dimensions[attr.id]);
    const value = isNaN(raw) ? 1 : clamp(raw, min, max);
    return acc * value;
  }, 1);

  // Dimension and service previews scale the backend-authoritative price tiers.
  const scalePrice = (value: number | undefined) =>
    value == null ? undefined : roundPrice(value * multiplier);
  const adjustedCurrentWithoutVat = scalePrice(priceDisplay.currentWithoutVat);
  const adjustedCurrentWithVat = scalePrice(priceDisplay.currentWithVat);
  const adjustedBeforeDiscountWithoutVat = scalePrice(priceDisplay.beforeDiscountWithoutVat);
  const adjustedBeforeDiscountWithVat = scalePrice(priceDisplay.beforeDiscountWithVat);
  const adjustedMocWithoutVat = scalePrice(priceDisplay.catalogRetailWithoutVat);
  const adjustedMocWithVat = scalePrice(priceDisplay.catalogRetailWithVat);
  const adjustedServicesPrice = roundPrice(getServicesPrice());
  const adjustedServicesPriceVat = roundPrice(getServicesPrice(true));
  const mainPrice = priceWithoutVatFirst
    ? adjustedCurrentWithoutVat == null
      ? undefined
      : adjustedCurrentWithoutVat + adjustedServicesPrice
    : adjustedCurrentWithVat == null
      ? undefined
      : adjustedCurrentWithVat + adjustedServicesPriceVat;
  const secondaryPrice = priceWithoutVatFirst
    ? adjustedCurrentWithVat == null
      ? undefined
      : adjustedCurrentWithVat + adjustedServicesPriceVat
    : adjustedCurrentWithoutVat == null
      ? undefined
      : adjustedCurrentWithoutVat + adjustedServicesPrice;
  const beforeDiscountPrice = priceWithoutVatFirst
    ? adjustedBeforeDiscountWithoutVat == null
      ? undefined
      : adjustedBeforeDiscountWithoutVat + adjustedServicesPrice
    : adjustedBeforeDiscountWithVat == null
      ? undefined
      : adjustedBeforeDiscountWithVat + adjustedServicesPriceVat;
  const mocPrice = priceWithoutVatFirst
    ? adjustedMocWithoutVat == null
      ? undefined
      : adjustedMocWithoutVat + adjustedServicesPrice
    : adjustedMocWithVat == null
      ? undefined
      : adjustedMocWithVat + adjustedServicesPriceVat;

  return (
    <div
      className={cn(
        'ea-product-with-dimensions-detail-price w-full rounded-md border border-border px-4 py-8 pr-0 md:px-8',
        'flex flex-col gap-4 sm:flex-row',
      )}
      style={{ boxShadow: '2px 2px 11px 0 rgba(0, 0, 0, 0.1)' }}
    >
      {/* Dimensions Inputs Column */}
      {rangeAttributes.length > 0 && (
        <div className="ea-product-with-dimensions-detail-price__dimensions-inputs flex flex-col gap-2">
          {rangeAttributes.map(attr => {
            const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
            let max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
            if (hasServiceRestriction) {
              max -= 0.038;
            }
            const currentValue = dimensions[attr.id];
            const parsed = parseFloat(currentValue);
            const error =
              currentValue !== '' && !isNaN(parsed) && (parsed < min || parsed > max)
                ? `${t('text1994')} ${min} - ${max}`
                : null;
            // Use attribute.values[0].name as unit if defined, else default to "m"
            const attrUnit =
              attr.values && attr.values[0] && attr.values[0].name ? attr.values[0].name : 'm';
            return (
              <div
                key={attr.id}
                className="ea-product-with-dimensions-detail-price__dimension-input flex flex-col items-center md:items-start"
              >
                <div className="flex items-center gap-4">
                  <label
                    htmlFor={`dimension-${attr.id}`}
                    className={cn('mb-1 text-xs', error && 'text-destructive-foreground')}
                    style={{
                      minWidth: '132px',
                    }}
                  >
                    {t('text1995')} <strong>{attr.name.toLowerCase()}</strong>
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      id={`dimension-${attr.id}`}
                      value={currentValue}
                      onChange={e => handleDimensionChange(attr.id.toString(), e.target.value)}
                      className={{
                        wrapper: cn(
                          'h-8 w-[80px] rounded-[4px] border border-border',
                          error ? 'outline outline-[2px] outline-destructive-foreground' : '',
                        ),
                        main: cn('rounded-[2px]', error ? 'text-destructive-foreground' : ''),
                      }}
                    />
                    <span
                      className={cn('text-sm font-bold', error && 'text-destructive-foreground')}
                    >
                      {attrUnit}
                    </span>
                  </div>
                </div>
                <div className="">
                  {error && (
                    <span className="whitespace-nowrap text-[12px] font-semibold text-destructive-foreground">
                      {error}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Price Display Column */}
      <div
        className={cn(
          'flex w-full flex-col items-center justify-center',
          'ea-product-with-dimensions-detail-price',
          'flex-1',
        )}
      >
        <div className="flex flex-col items-end">
          {!selectedVariant && !!variants?.length ? (
            renderErrorMessage()
          ) : priceDisplay.priceOnRequest ? null : (
            <>
              {discount > 0 && (
                <Badge
                  size="md"
                  className={cn(
                    'flex select-none items-center gap-1 rounded-md bg-destructive font-semibold text-destructive-foreground hover:bg-destructive',
                    'ea-product-price__discount',
                  )}
                >
                  {React.cloneElement(PRODUCT_CONFIG.productPrice.discountIcon as any, {
                    className: cn('ea-product-price__discount-icon'),
                  })}
                  {`${t('text24')} -${priceDisplay.discountLabel}%`}
                </Badge>
              )}
              <div
                className={cn('flex flex-row items-center', 'ea-product-detail-price__container')}
              >
                <div>
                  {priceDisplay.showMocVocPrices && mocPrice != null ? (
                    <div className="flex">
                      {`${t('text1926')} ${formatPrice(
                        mocPrice,
                        currency,
                        currencySettings,
                      )}${unit}`}
                    </div>
                  ) : null}
                  <span
                    className={cn(
                      'whitespace-nowrap text-[32px] font-bold leading-[37.5px]',
                      'ea-product-detail-price__after-discount',
                    )}
                  >
                    <strong>{`${formatPrice(
                      mainPrice,
                      currency,
                      currencySettings,
                    )}${unit}`}</strong>
                  </span>
                </div>
                {discount > 0 && (
                  <span
                    className={cn(
                      'ml-2 text-[24px] leading-[28px]',
                      'ea-product-detail-price__with-vat',
                    )}
                  >
                    <del>{`${formatPrice(
                      beforeDiscountPrice,
                      currency,
                      currencySettings,
                    )}${unit}`}</del>
                  </span>
                )}
              </div>
              {settings.product_detail?.show_price_without_vat?.enabled && (
                <span
                  className={cn('text-muted-foreground', 'ea-product-detail-price__without-vat')}
                >
                  {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                  {formatPrice(secondaryPrice, currency, currencySettings)}{' '}
                  {priceWithoutVatFirst ? t('text1122') : t('text443')} {unit}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
